import { Link, useNavigate } from 'react-router-dom'
import type { Church } from '@fellowship/shared'
import { DENOM_SHORT, GRADIENT, SHADOW_SOFT } from './format'

function ChurchMonogram({ church, size, radius }: { church: Church; size: number; radius: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        flexShrink: 0,
        background: GRADIENT,
        color: '#fff',
        fontWeight: 700,
        fontSize: size * 0.35,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {church.logoUrl ? (
        <img src={church.logoUrl} alt={church.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        church.name.charAt(0)
      )}
    </div>
  )
}

interface FollowedChurchCardProps {
  church: Church
  /** Compact single-line row for the Overview rail. */
  compact?: boolean
}

export function FollowedChurchCard({ church, compact }: FollowedChurchCardProps) {
  const navigate = useNavigate()
  const locationLabel = [church.city, church.state].filter(Boolean).join(', ')

  if (compact) {
    return (
      <Link
        to={`/churches/${church.slug}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          borderRadius: 14,
          background: 'var(--surface)',
          border: '1px solid var(--line-soft)',
          textDecoration: 'none',
        }}
      >
        <ChurchMonogram church={church} size={36} radius={999} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>{church.name}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>
            {[church.denomination ? DENOM_SHORT[church.denomination] ?? church.denomination : null, `${church.followerCount} followers`]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
      </Link>
    )
  }

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line-soft)',
        borderRadius: 20,
        boxShadow: SHADOW_SOFT,
        padding: 18,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <ChurchMonogram church={church} size={46} radius={14} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{church.name}</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>
            {[locationLabel, `${church.followerCount} followers`].filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button
          type="button"
          disabled
          style={{
            flex: 1,
            background: 'var(--surface)',
            color: 'var(--ink-2)',
            border: '1px solid var(--line-soft)',
            borderRadius: 999,
            padding: '8px 0',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'default',
          }}
        >
          Following
        </button>
        <button
          type="button"
          onClick={() => navigate(`/churches/${church.slug}`)}
          style={{
            flex: 1,
            background: 'var(--surface-2)',
            color: 'var(--ink)',
            border: '1px solid var(--line-soft)',
            borderRadius: 999,
            padding: '8px 0',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          View events
        </button>
      </div>
    </div>
  )
}
